import type { RunKind, RunParts, RunStart, Surface } from "./types.js";

/** `on grass`, `on the track` at the end of a run's words. */
export const SURFACE_PHRASE = /\s*\bon\s+(the\s+)?(grass|turf|track|sand)\b\s*$/i;

const kinds: [RunKind, RegExp][] = [
  ["resisted", /(?<![\w-])(resisted|sled|1080)(?![\w-])/i],
  ["slowBuild", /(?<![\w-])slow\s+build(?![\w-])/i],
  ["tempo", /(?<![\w-])tempo(?![\w-])/i],
  ["accel", /(?<![\w-])accel(s|eration|erations)?(?![\w-])/i],
  ["skips", /(?<![\w-])skips?(?![\w-])/i],
  ["timeTrial", /(?<![\w-])(tt|time\s+trials?)(?![\w-])/i],
  ["sprint", /(?<![\w-])sprints?(?![\w-])/i],
];

const starts: [RunStart, RegExp][] = [
  ["blocks", /\b(from\s+)?(the\s+)?blocks?\b/i],
  ["3-point", /\b3[\s-]?points?(\s+start)?\b/i],
  ["walk-in", /\bwalk-in(\s+start)?\b/i],
  ["standing", /\bfrom\s+(a\s+)?standing(\s+start|\s+position)?\b/i],
  ["fly", /\b(fly|flying)\b/i],
];

/** What a run's words (and its cues, for a sled or the 1080) say it is. */
export function runPartsOf(name: string, cues: string[]): RunParts {
  const all = [name, ...cues].join(" ");
  // A sled or the 1080 is usually in the brackets: `3x20m sprint (blocks) - 1080 (medium)`.
  const kind = kinds.find(([k, pattern]) => pattern.test(k === "resisted" ? all : name))?.[0] ?? "run";
  const parts: RunParts = { kind };
  const start = starts.find(([, pattern]) => pattern.test(all))?.[0];
  if (start !== undefined) parts.start = start;
  if (/\b(uphill|hills?)\b/i.test(name)) parts.uphill = true;
  const surface = surfaceIn(all);
  if (surface !== undefined) parts.surface = surface;
  return parts;
}

function surfaceIn(text: string): Surface | undefined {
  const match = /\b(?:on\s+(?:the\s+)?)?(grass|turf|track|sand)\b/i.exec(text);
  if (match === null) return undefined;
  // `on track with soft shoes` is a cue; `(grass)` and `on grass` are the surface.
  return match[1]!.toLowerCase() as Surface;
}

/**
 * A run's words without what its fields say, taken off only where he writes them in the order and
 * spelling the form writes back: `accel uphill from walk-in start with board overhead` → `with board
 * overhead`. Words in any other order stay his (`fast tempo`, `from 3 point start`).
 */
export function runWords(name: string, cues: string[]): { parts: RunParts; words: string } {
  const parts = runPartsOf(name, cues);
  let words = name.trim();
  const take = (pattern: RegExp) => {
    const m = pattern.exec(words);
    if (m === null) return false;
    words = (words.slice(0, m.index) + words.slice(m.index + m[0].length)).replace(/\s+/g, " ").trim();
    return true;
  };
  const word = kindWord[parts.kind];
  // `resisted sprint` is the resisted run's own name.
  const tail = parts.kind === "resisted" ? "(\\s+sprints?)?" : "s?";
  if (word !== "") take(new RegExp(`^${word.replace(" ", "\\s+")}${tail}(?![\\w-])\\s*`, "i"));
  if (parts.uphill) take(/^uphill(?![\w-])\s*/i);
  if (parts.start !== undefined) take(new RegExp(`^${startWords[parts.start]}(?![\\w-])\\s*`, "i"));
  if (parts.surface !== undefined) take(new RegExp(`\\s*\\bon\\s+${parts.surface}$`, "i"));
  return { parts, words };
}

const kindWord: Record<RunKind, string> = {
  sprint: "sprint", accel: "accel", tempo: "tempo", resisted: "resisted", skips: "skip", slowBuild: "slow build", timeTrial: "TT", run: "",
};
const startWords: Record<RunStart, string> = {
  blocks: "from blocks", "3-point": "from 3-point", "walk-in": "from walk-in start", standing: "from standing start", fly: "fly",
};

/** A run's words with its fields written in, each only when the rest doesn't already say it. */
export function runText(parts: RunParts, words: string, cues: string[]): string {
  const said = runPartsOf(words, cues);
  // A sled or the 1080 in the brackets says resisted only of words that already name a run.
  const named = runPartsOf(words, []).kind !== "run";
  const out: string[] = [];
  if ((said.kind !== parts.kind || !named) && kindWord[parts.kind] !== "") out.push(kindWord[parts.kind]);
  if (parts.uphill && !said.uphill) out.push("uphill");
  if (parts.start !== undefined && said.start !== parts.start) out.push(startWords[parts.start]);
  if (words.trim() !== "") out.push(words.trim());
  if (parts.surface !== undefined && said.surface !== parts.surface) out.push(`on ${parts.surface}`);
  return out.join(" ");
}
